
import React, { useState, useEffect, useRef } from 'react';
import countriesData from '../data/countries.json';


function getCountryByCode(code: string) {
  return countriesData.find((c: any) => c.code.toLowerCase() === code.toLowerCase()) || countriesData.find((c: any) => c.code === 'IN');
}

interface Country {
  name: string;
  code: string;
  dial_code: string;
}

interface CustomPhoneInputProps {
  value: string;
  onChange: (val: string) => void;
  inputClassName?: string;
  onCountryChange?: (code: string) => void;
  [key: string]: any;
}

export default function CustomPhoneInput({ value, onChange, inputClassName, onCountryChange, ...props }: CustomPhoneInputProps) {
  const [open, setOpen] = useState(false);
  const [country, setCountry] = useState(() => countriesData.find((c: any) => c.code === 'IN') || countriesData[0]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const defaultCountry = countriesData.find((c: any) => c.code === 'IN') || countriesData[0];
    setCountry(defaultCountry);
    if (onCountryChange) onCountryChange(defaultCountry.dial_code);
  }, []);

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (!dropdownRef.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  return (
    <>
      <div
        className={`input-wrapper phone-input-wrapper ${inputClassName || ''}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          height: 52,
          width: '100%',
          borderRadius: 14,
          padding: '0 16px',
          background: '#fff',
          border: '1px solid #e0e0e0',
          boxSizing: 'border-box',
          gap: 12,
          position: 'relative',
        }}
      >
        {/* Prefix group: country code and dial, flat structure, no inner box */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexShrink: 0,
          fontSize: '1rem',
          color: '#222',
          lineHeight: 1.2
        }}>
          <button
            type="button"
            className="flag-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
              padding: 0,
              cursor: 'pointer',
              height: '100%',
              borderRadius: 0,
              boxSizing: 'border-box',
              margin: 0,
              fontSize: '1em',
              color: '#222',
              fontWeight: 400,
            }}
            onClick={() => setOpen(o => !o)}
            tabIndex={0}
            aria-label="Select country"
          >
            <span style={{fontSize: '1em', lineHeight: 1, color: '#222', fontWeight: 400}}>{country.code.toUpperCase()}</span>
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#888" style={{marginLeft:2}}><path d="M6 9l6 6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <span style={{fontSize: '1em', color: '#222', fontWeight: 400}}>{country.dial_code}</span>
        </div>
        {/* Input field: flat, no inner box */}
        <input
          ref={inputRef}
          type="tel"
          className="phone-input"
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: '1rem',
            color: '#222',
            fontWeight: 400,
            height: '100%',
            padding: 0,
            margin: 0,
            width: '100%',
            boxSizing: 'border-box',
          }}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Enter phone number"
          {...props}
        />
        {open && (
          <div
            ref={dropdownRef}
            className="custom-phone-dropdown absolute left-0 top-full z-10 w-full bg-white border border-gray-200 rounded-xl shadow-lg mt-2 py-2"
            style={{ maxHeight: 250, overflowY: 'auto' }}
          >
            {countriesData.map((c: any) => (
              <div
                key={c.code}
                className="dropdown-item flex items-center gap-2 px-3 py-2 hover:bg-blue-50 cursor-pointer rounded-lg"
                style={{padding:'10px 14px',cursor:'pointer',borderRadius:10,transition:'background 0.15s'}}
                onClick={() => { setCountry(c); setOpen(false); if (onCountryChange) onCountryChange(c.dial_code); }}
                onMouseDown={e => e.preventDefault()}
              >
                {/* Optionally add flag emoji if you have a mapping */}
                <span style={{fontWeight:500}}>{c.name}</span>
                <span style={{marginLeft:'auto',color:'#888'}}>{c.dial_code}</span>
              </div>
            ))}
          </div>
        )}
        {/* Removed inline <style> block to prevent double border/focus. Use only global CSS for input-wrapper and phone-input. */}
      </div>
    </>
  );
}
